import { EventTypes } from '@racemap/sdk/schema/events';
import classnames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import VirtualList, { type ItemStyle } from 'react-tiny-virtual-list';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { ALL_TAG, NULL_TAG } from 'src/lib/player/constants';
import type { EventState, PoiType, StarterType, TrackType } from 'src/lib/player/store';
import { findAllIndexes, matchesTagSelection } from 'src/lib/player/utils';
import { useMeasure } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import SettingsView from './SettingsView';
import ConnectedStarterListItem from './SideBar/ConnectedStarterListItem';
import EventListItem from './SideBar/EventListItem';
import { EventsHeader } from './SideBar/EventsHeader';
import { FavoriteHeader } from './SideBar/FavoriteHeader';
import { ParticipantsFilter } from './SideBar/ParticipantsFilter';
import { PoiHeader } from './SideBar/PoiHeader';
import PoiListItem from './SideBar/PoiListItem';
import { SideBarSearchForm } from './SideBar/SideBarSearchForm';
import { StarterGroupHeader } from './SideBar/StarterGroupHeader';
import { StarterHeader } from './SideBar/StarterHeader';
import { StarterSortRow } from './SideBar/StarterSortRow';
import { TrackHeader } from './SideBar/TrackHeader';
import TrackListItem from './SideBar/TrackListItem';
import { SideBarItemType, SideBarSection, type StarterSorting } from './SideBar/types';
import { useSideBarItems } from './SideBar/useSideBarItems';
import { deduplicateStarters, toggleSelectedTag } from './SideBar/utils';

const ITEM_HEIGHT = 59;
export const HEADER_HEIGHT = 49;

enum SideBarTab {
  SEARCH = 'SEARCH',
  SETTINGS = 'SETTINGS',
}

export type SideBarProps = {
  onClose: () => void;
  allEvents: Array<EventState>;
  currentEventIndices: Array<number>;
  eventType: EventTypes;
  allCurrentStarters: Array<StarterType>;
  allCurrentTracks: Array<TrackType>;
  allCurrentPois: Array<PoiType>;
  currentSelectedIds: Array<string>;
  hiddenIds: Set<string>;
  selectedTags: Map<string, Array<string>>;
  availableTags: Map<string, Array<[string, number]>>;
  availableStarterGroups: Map<string, Array<[string, number]>>;
  renderInfoBuffer: RenderInfoBuffer;
  onSetSelected: (ids: Array<string>) => void;
  onSetSelectedTags: (selectedTags: Map<string, Array<string>>) => void;
  onHide: (ids: Array<string>) => void;
  onUnhide: (ids: Array<string>) => void;
  autoReloadEnabled?: boolean;
  isReloading?: boolean;
  onReload?: () => Promise<void>;
};

export default function SideBar({
  onClose,
  allEvents,
  currentEventIndices,
  eventType,
  allCurrentStarters,
  allCurrentTracks,
  allCurrentPois,
  currentSelectedIds,
  hiddenIds,
  selectedTags,
  availableTags,
  availableStarterGroups,
  renderInfoBuffer,
  onSetSelected,
  onSetSelectedTags,
  onHide,
  onUnhide,
  autoReloadEnabled,
  isReloading,
  onReload,
}: SideBarProps) {
  const [listContainerRef, listContainerBounds] = useMeasure();
  const listScrollTopRef = useRef(0);
  const listRef = useRef<VirtualList>(null);

  const showEventSelector =
    (eventType === EventTypes.CONTEST_GROUP || eventType === EventTypes.STAGE_GROUP) &&
    allEvents.length > 0;

  const [searchText, setSearchText] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<SideBarTab>(SideBarTab.SEARCH);
  const [isFilterOpen, setIsFilterOpen] = useState(selectedTags.size > 0);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [openSections, setOpenSections] = useState(
    showEventSelector
      ? [SideBarSection.EVENTS, SideBarSection.STARTERS, SideBarSection.FAVORITE]
      : [SideBarSection.STARTERS, SideBarSection.FAVORITE],
  );
  const [openStarterGroups, setOpenStarterGroups] = useState<Array<string>>([]);
  const [starterSorting, setStarterSorting] = useState<StarterSorting | null>(null);

  const filteredStarters = useMemo(() => {
    return selectedTags.size === 0
      ? allCurrentStarters
      : allCurrentStarters.filter((starter) => matchesTagSelection(starter.tags, selectedTags));
  }, [allCurrentStarters, selectedTags]);

  const deduplicatedStarters = useMemo(
    () => deduplicateStarters(filteredStarters),
    [filteredStarters],
  );

  const onToggleSection = useCallback(
    (section: SideBarSection) => {
      if (openSections.includes(section)) {
        setOpenSections(openSections.filter((s) => s !== section));
      } else {
        setOpenSections([section, ...openSections]);
      }
    },
    [openSections],
  );
  const onToggleStarterGroup = useCallback(
    (tagKey: string, tagValue: string) => {
      const key = `${tagKey}-${tagValue}`;
      if (openStarterGroups.includes(key)) {
        setOpenStarterGroups(openStarterGroups.filter((s) => s !== key));
      } else {
        setOpenStarterGroups([...openStarterGroups, key]);
      }
    },
    [openStarterGroups],
  );

  function calculateSectionOffset(sectionLabel: SideBarSection): number {
    let offset = 0;
    for (const item of items) {
      if (item.type === SideBarItemType.HEADER && item.label === sectionLabel) {
        return offset;
      }
      switch (item.type) {
        case SideBarItemType.HEADER:
          offset += HEADER_HEIGHT;
          break;
        case SideBarItemType.STARTER_GROUP:
          offset += HEADER_HEIGHT;
          break;
        default:
          offset += ITEM_HEIGHT;
          break;
      }
    }
    return 0;
  }

  function switchToSearchTab() {
    unstable_batchedUpdates(() => {
      if (currentTab !== SideBarTab.SEARCH) {
        setCurrentTab(SideBarTab.SEARCH);
      }
    });
  }

  function toggleSettingsTab() {
    setCurrentTab((currentTag) =>
      currentTag === SideBarTab.SETTINGS ? SideBarTab.SEARCH : SideBarTab.SETTINGS,
    );
  }

  function scrollCorrection(_: unknown, isSelected: boolean) {
    if (openSections.includes(SideBarSection.FAVORITE)) {
      if (listRef.current == null) return;

      const newScrollTop = isSelected
        ? listScrollTopRef.current + ITEM_HEIGHT
        : listScrollTopRef.current - ITEM_HEIGHT;
      listRef.current.rootNode.scrollTo({
        top: newScrollTop,
        behavior: 'instant',
      });
      listScrollTopRef.current = newScrollTop;
    }
  }

  function handleToggleSort() {
    const sortBoxOffset = calculateSectionOffset(SideBarSection.STARTERS) + HEADER_HEIGHT;
    const sortBoxIsInView =
      listScrollTopRef.current + HEADER_HEIGHT <= sortBoxOffset &&
      sortBoxOffset <= listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);
    if (isSortOpen && sortBoxIsInView) {
      setIsSortOpen(false);
    } else {
      setIsSortOpen(true);
      setIsFilterOpen(false);
      if (!sortBoxIsInView) {
        listRef.current?.rootNode.scrollTo({
          top: sortBoxOffset - HEADER_HEIGHT,
          behavior: 'smooth',
        });
        listScrollTopRef.current = sortBoxOffset - HEADER_HEIGHT;
      }
    }
  }

  function handleToggleFilter() {
    const filterBoxOffset = calculateSectionOffset(SideBarSection.STARTERS) + HEADER_HEIGHT;
    const filterBoxIsInView =
      listScrollTopRef.current + HEADER_HEIGHT <= filterBoxOffset &&
      filterBoxOffset <= listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);
    if (isFilterOpen && filterBoxIsInView) {
      setIsFilterOpen(false);
    } else {
      setIsFilterOpen(true);
      setIsSortOpen(false);
      if (!filterBoxIsInView) {
        listRef.current?.rootNode.scrollTo({
          top: filterBoxOffset - HEADER_HEIGHT,
          behavior: 'smooth',
        });
        listScrollTopRef.current = filterBoxOffset - HEADER_HEIGHT;
      }
    }
  }

  function renderEventRow(event: EventState, style: ItemStyle) {
    return (
      <div key={event.eventId} style={style}>
        <EventListItem event={event} />
      </div>
    );
  }

  function renderStarterRow(
    starter: StarterType,
    style: ItemStyle,
    isFavorite = false,
    isGroup: string | null = null,
  ) {
    const renderIndex = renderInfoBuffer.getRenderIndex(starter.eventIndex, starter.index);
    return (
      <div key={`${starter.id}-${isFavorite}-${isGroup}`} style={style}>
        <ConnectedStarterListItem
          starter={starter}
          starterOpacity={
            currentEventIndices.includes(starter.eventIndex) && !hiddenIds.has(starter.id)
              ? renderInfoBuffer.getOpacity(renderIndex)
              : 0
          }
          starterOffset={renderInfoBuffer.getOffset(renderIndex)}
          starterTimestamp={renderInfoBuffer.getTimestamp(renderIndex)}
          onClick={scrollCorrection}
        />
      </div>
    );
  }

  function renderPoiRow(poi: PoiType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`poi-${poi.id}-${isFavorite}`} style={style}>
        <PoiListItem poi={poi} onClick={scrollCorrection} />
      </div>
    );
  }

  function renderTrackRow(track: TrackType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`track-${track.id}-${isFavorite}`} style={style}>
        <TrackListItem track={track} onClick={scrollCorrection} />
      </div>
    );
  }

  const items = useSideBarItems({
    searchText,
    openSections,
    openStarterGroups,
    isFilterOpen,
    isSortOpen,
    starterSorting,
    renderInfoBuffer,
    showEventSelector,
    eventType,
    allEvents,
    allCurrentStarters,
    allCurrentTracks,
    allCurrentPois,
    currentSelectedIds,
    availableTags,
    availableStarterGroups,
    filteredStarters,
    deduplicatedStarters,
  });

  const stickyIndices = findAllIndexes(items, (item) => item.type === SideBarItemType.HEADER);

  return (
    <div className="side-bar">
      <SideBarSearchForm
        searchText={searchText}
        isSettingsTabActive={currentTab === SideBarTab.SETTINGS}
        onSearchTextChange={setSearchText}
        onClose={onClose}
        onSwitchToSearchTab={switchToSearchTab}
        onToggleSettingsTab={toggleSettingsTab}
        autoReloadEnabled={autoReloadEnabled}
        isReloading={isReloading}
        onReload={onReload}
      />
      <div ref={listContainerRef} style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {currentTab === SideBarTab.SEARCH ? (
          <VirtualList
            ref={listRef}
            width="100%"
            height={listContainerBounds.height}
            style={{ flex: '1' }}
            className="starter-list"
            itemCount={items.length}
            itemSize={(index: number) => {
              const item = items[index];
              switch (item.type) {
                case SideBarItemType.HEADER:
                  return HEADER_HEIGHT;
                case SideBarItemType.STARTER_GROUP:
                  return HEADER_HEIGHT;
                case SideBarItemType.STARTER_FILTER:
                  return 40 + item.itemCount * 40 + 1;
                case SideBarItemType.STARTER_SORT:
                  return 40 + 3 * 40 + 1;
                default:
                  return ITEM_HEIGHT;
              }
            }}
            stickyIndices={stickyIndices}
            overscanCount={20}
            estimatedItemSize={ITEM_HEIGHT}
            onScroll={(scrollOffset: number) => {
              listScrollTopRef.current = scrollOffset;
            }}
            renderItem={({ index, style }: { index: number; style: ItemStyle }) => {
              const item = items[index];
              switch (item.type) {
                case SideBarItemType.HEADER:
                  switch (item.label) {
                    case SideBarSection.FAVORITE: {
                      const anySelected = currentSelectedIds.length > 0;
                      return (
                        <FavoriteHeader
                          key="header-FAVORITE"
                          style={style}
                          isOpen={openSections.includes(SideBarSection.FAVORITE) && anySelected}
                          anySelected={anySelected}
                          allHidden={
                            anySelected && currentSelectedIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SideBarSection.FAVORITE)}
                          onClearSelection={() => onSetSelected([])}
                          onHide={() => onHide(currentSelectedIds)}
                          onUnhide={() => onUnhide(currentSelectedIds)}
                        />
                      );
                    }
                    case SideBarSection.TRACK: {
                      const trackIds = allCurrentTracks.map((track) => track.id);
                      return (
                        <TrackHeader
                          key="header-TRACK"
                          style={style}
                          isOpen={openSections.includes(SideBarSection.TRACK)}
                          allHidden={
                            trackIds.length > 0 && trackIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SideBarSection.TRACK)}
                          onHide={() => onHide(trackIds)}
                          onUnhide={() => onUnhide(trackIds)}
                        />
                      );
                    }
                    case SideBarSection.POI: {
                      const poiIds = allCurrentPois.map((poi) => poi.id);
                      return (
                        <PoiHeader
                          key="header-POI"
                          style={style}
                          isOpen={openSections.includes(SideBarSection.POI)}
                          allHidden={poiIds.length > 0 && poiIds.every((id) => hiddenIds.has(id))}
                          onToggle={() => onToggleSection(SideBarSection.POI)}
                          onHide={() => onHide(poiIds)}
                          onUnhide={() => onUnhide(poiIds)}
                        />
                      );
                    }
                    case SideBarSection.STARTERS: {
                      const starterIds = allCurrentStarters.map((s) => s.id);
                      return (
                        <StarterHeader
                          key="header-STARTERS"
                          style={style}
                          isOpen={openSections.includes(SideBarSection.STARTERS)}
                          filteredCount={filteredStarters.length}
                          totalCount={allCurrentStarters.length}
                          hasSelectedTags={selectedTags.size > 0}
                          isSortOpen={isSortOpen}
                          isFilterOpen={isFilterOpen}
                          hasAvailableTags={availableTags.size > 0}
                          allHidden={
                            starterIds.length > 0 && starterIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SideBarSection.STARTERS)}
                          onToggleSort={handleToggleSort}
                          onToggleFilter={handleToggleFilter}
                          onHide={() => onHide(starterIds)}
                          onUnhide={() => onUnhide(starterIds)}
                        />
                      );
                    }
                    case SideBarSection.EVENTS:
                      return (
                        <EventsHeader
                          key="header-EVENT"
                          style={style}
                          isOpen={openSections.includes(SideBarSection.EVENTS)}
                          eventType={item.eventType}
                          onToggle={() => onToggleSection(SideBarSection.EVENTS)}
                        />
                      );
                    default:
                      return null;
                  }
                case SideBarItemType.EVENT:
                  return renderEventRow(item.event, style);
                case SideBarItemType.STARTER:
                  return renderStarterRow(item.starter, style, item.isFavorite, item.group);
                case SideBarItemType.STARTER_FILTER:
                  return (
                    <ParticipantsFilter
                      availableTags={availableTags}
                      itemStyle={style}
                      selectedTags={selectedTags}
                      onToggleTag={(tagKey, value) => {
                        onSetSelectedTags(toggleSelectedTag(selectedTags, tagKey, value));
                      }}
                      onReset={() => {
                        onSetSelectedTags(new Map());
                        setIsFilterOpen(false);
                      }}
                    />
                  );
                case SideBarItemType.STARTER_SORT:
                  return (
                    <StarterSortRow
                      key="starter-sort-row"
                      style={style}
                      starterSorting={starterSorting}
                      onSort={(sortKey, direction) => setStarterSorting([sortKey, direction])}
                      onReset={() => {
                        setStarterSorting(null);
                        setIsSortOpen(false);
                      }}
                    />
                  );
                case SideBarItemType.STARTER_GROUP: {
                  const startersInGroup =
                    item.tagKey === ALL_TAG
                      ? deduplicatedStarters
                      : deduplicatedStarters.filter(
                          (starter) =>
                            starter.tags.get(item.tagKey) === item.tagValue ||
                            (item.tagValue === NULL_TAG && !starter.tags.has(item.tagKey)),
                        );
                  const groupIds = startersInGroup.map((s) => s.id);
                  return (
                    <StarterGroupHeader
                      key={`header-STARTER-GROUP-${item.tagKey}-${item.tagValue}`}
                      style={style}
                      tagKey={item.tagKey}
                      tagValue={item.tagValue}
                      isOpen={openStarterGroups.includes(`${item.tagKey}-${item.tagValue}`)}
                      starterCount={startersInGroup.length}
                      allHidden={groupIds.length > 0 && groupIds.every((id) => hiddenIds.has(id))}
                      onToggle={() => onToggleStarterGroup(item.tagKey, item.tagValue)}
                      onHide={() => onHide(groupIds)}
                      onUnhide={() => onUnhide(groupIds)}
                    />
                  );
                }
                case SideBarItemType.POI:
                  return renderPoiRow(item.poi, style, item.isFavorite);
                case SideBarItemType.TRACK:
                  return renderTrackRow(item.track, style, item.isFavorite);
                default:
                  return null;
              }
            }}
          />
        ) : (
          <SettingsView />
        )}
      </div>
    </div>
  );
}

export function SideBarButton({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className={classnames('search-button-container', { 'is-open': isOpen })}>
      <div
        className="search-button"
        {...onClickGenerator(() => {
          onToggle();
        })}
      >
        <i className="fas fa-search" />
      </div>
    </div>
  );
}
